import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { PocketPanel } from "@/components/ui";
import { openNotificationItem } from "@/components/panels/notifications/notification-item";
import { NotificationList } from "@/components/panels/notifications/notification-list";
import { notificationSections } from "@/components/panels/notifications/notification-sections";
import { useNotificationActions } from "@/components/panels/notifications/use-notification-actions";
import { usePopoverDismiss } from "@/hooks/use-popover-dismiss";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { wallMessages } from "@/i18n/messages/wall";
import { useNotificationStore } from "@/stores/notification-store";

interface WallNotificationsProps {
  /** The wall's search: the list shows the rows it matches. */
  query: string;
  /** The field and its dot: a press there is not a click outside. */
  anchorRef: RefObject<HTMLElement | null>;
  /** The dot, which takes focus back when the list closes. */
  returnFocusRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/**
 * The notification list as a dropdown under the wall's search (mockup
 * `.nmenu`): every project's Needs you, Today and Earlier, narrowed by the
 * search. A row opens its target and closes the list; Esc and a press
 * outside close it. The first row takes focus once rows show, and focus
 * goes back to the dot as the list closes, unless it has moved elsewhere.
 */
export function WallNotifications({
  query,
  anchorRef,
  returnFocusRef,
  onClose,
}: WallNotificationsProps) {
  const t = useT(wallMessages);
  const tCommon = useT(commonMessages);
  const tHeader = useT(panelsHeaderMessages);
  const tSearch = useT(panelsSearchMessages);
  const contentRef = useRef<HTMLDivElement>(null);
  const actions = useNotificationActions();
  const summary = useNotificationStore((state) => state.summary);
  const loading = useNotificationStore((state) => state.summaryLoading);
  const fetchSummary = useNotificationStore((state) => state.fetchSummary);
  const [openedAt] = useState(Date.now);
  usePopoverDismiss({
    open: true,
    onClose,
    anchorRef,
    contentRef,
    stopEscapePropagation: true,
  });
  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);
  const sections = useMemo(
    () =>
      notificationSections(summary, {
        query: query.trim(),
        projectId: null,
        now: openedAt,
      }),
    [openedAt, query, summary],
  );
  const hasRows = sections.length > 0;
  const rowFocusedRef = useRef(false);
  useEffect(() => {
    if (!hasRows || rowFocusedRef.current) return;
    rowFocusedRef.current = true;
    contentRef.current
      ?.querySelector<HTMLElement>("button:not(:disabled)")
      ?.focus({ preventScroll: true });
  }, [hasRows]);
  useEffect(() => {
    const content = contentRef.current;
    const returnTo = returnFocusRef.current;
    return () => {
      const active = document.activeElement;
      if (!active || active === document.body || content?.contains(active)) {
        returnTo?.focus({ preventScroll: true });
      }
    };
  }, [returnFocusRef]);

  return (
    <PocketPanel
      ref={contentRef}
      nativeGroup="wall-notifications"
      nativeSurface={false}
      testId="wall-notifications"
      role="dialog"
      aria-label={tHeader("notifications")}
      data-interactive="true"
      className="scrollbar-none absolute left-1/2 top-[calc(100%+8px)] z-30 max-h-[min(560px,70vh)] w-[min(420px,calc(100vw-32px))] -translate-x-1/2 overflow-y-auto overscroll-contain"
    >
      {hasRows ? (
        <NotificationList
          sections={sections}
          query={query.trim()}
          actions={actions}
          onOpen={(item) => {
            if (openNotificationItem(item, actions)) onClose();
          }}
        />
      ) : (
        <p
          className="px-2 py-3 text-appkit-subhead text-secondary"
          data-testid="wall-notifications-empty"
        >
          {loading
            ? tCommon("loading")
            : query.trim()
              ? t("noNotificationsMatch")
              : tSearch("caughtUp")}
        </p>
      )}
    </PocketPanel>
  );
}
